/**
 * "As needed" forms: forms tied to an EVENT rather than to a visit (serious
 * adverse events, protocol deviations, unscheduled visits, pregnancy
 * notification, and the like). They belong on no fixed visit, and a grid that
 * leaves them blank reads as "forgotten". Product owner, 2026-11-02: the
 * platform proposes "as needed" automatically, a person can change it.
 *
 * THE PROTOCOL WINS. A form the protocol, the matcher, the model or a person
 * placed on a visit is never proposed (visitMappingFill.ts checks the grid, this
 * file only recognises event forms). A person's choice wins too: a form they
 * switched on is theirs, and one they cleared is never proposed again.
 *
 * Stored on crfPackage/visitMapping beside autoTicks, standardTicks and
 * personCleared:
 *   asNeeded        form id -> "proposed" | "person" (where it came from)
 *   asNeededBasis   form id -> "name" | "category" | "model" (why it was proposed;
 *                   proposed forms only)
 *   asNeededCleared form ids a person took out of as-needed (never proposed again)
 *
 * THE RULE is deliberately narrow: the unmistakable cases, by well-known event
 * wording in the form's name or by the form's own category. It must work for any
 * study and any naming, and it must NOT take an ordinary scheduled kind (labs,
 * questionnaires, vitals, history, eligibility, a scheduled pregnancy test) for
 * an event form: a wrong "as needed" hides a form from every visit. Wording it
 * cannot foresee is left to the model (visitMatrixFill.ts), whose answer is
 * checked against the real forms.
 *
 * Pure. No Firestore.
 */

export type AsNeededSource = "proposed" | "person";
export type AsNeededBasis = "name" | "category" | "model";

export interface AsNeededForm {
  id: string;
  name: string;
  category?: string;
}

/** The stored fields, read defensively. */
export interface AsNeededRecord {
  asNeeded: Record<string, AsNeededSource>;
  asNeededBasis: Record<string, AsNeededBasis>;
  asNeededCleared: string[];
}

const norm = (s: unknown): string =>
  (typeof s === "string" ? s : "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/** Wording that names an event. Each is tested against the padded, normalised name. */
const EVENT_NAME_PATTERNS: RegExp[] = [
  / serious adverse | sae /, // serious adverse event(s), SAE report
  / adverse events? of special interest | special interest | aesi /,
  // a protocol deviation, or a deviation form/log/report on its own; "Deviation from Diet",
  // "Dose Deviation Accountability" and "Visit Deviation Window Check" are not (anchored to the whole name)
  /^ (?:(?:major|minor|significant|important|critical) )?(?:protocol )?(?:deviations?|violations?)(?: (?:log|form|report|reporting|tracker|tracking|record|listing|page))* $/,
  / unscheduled /,
  / unblinding | unblind /,
  / re ?screen/, // rescreening, re-screening (the leading space keeps pre-screening out)
  / (drug|treatment|product|medication|dose) (interruptions?|holds?) | interruptions? log /, // not a sleep-interruption scale
  / compensation | reimbursements? | stipends? /,
  / early (termination|withdrawal|discontinuation) | premature (termination|withdrawal|discontinuation) /,
  // a death reported as an event: "Death", "Deaths", "Date of Death", "Participant Deaths", "Death Summary",
  // "Death Case Report Form". Anchored to the whole name, so "Survival and Death Follow-up" and
  // "Death Certificate" (a document a site files, not an event-report form) are not.
  /^ (?:(?:participants?|subjects?|patients?) )?(?:date of )?deaths?(?: (?:report|reporting|form|information|summary|details|notification|case report form|event|events|page))* $/,
  /^ (?:report|notification|notice) of death $/,
];

/** A pregnancy FORM for an event: not the scheduled pregnancy test. */
const PREGNANCY_EVENT = / pregnan/;
/** A scheduled pregnancy TEST (or screen) is never the event form, whatever follows it in the name. */
const PREGNANCY_TEST = / (test|tests|testing) /;
const PREGNANCY_EVENT_WORDS = /notif|outcome|follow|report|exposure|partner/;

/** A form's own category saying it is not visit-driven. */
const AS_NEEDED_CATEGORY = /^(as needed|event driven|event based|unscheduled)$/;

/**
 * Why a form is an event form by the deterministic rule, or null: the form's
 * own category, else well-known event wording in its name.
 */
export function asNeededBasisOf(form: { name?: unknown; category?: unknown }): "name" | "category" | null {
  if (AS_NEEDED_CATEGORY.test(norm(form?.category))) return "category";
  const n = ` ${norm(form?.name)} `;
  if (n.trim() === "") return null;
  if (PREGNANCY_EVENT.test(n) && PREGNANCY_EVENT_WORDS.test(n) && !PREGNANCY_TEST.test(n) && !(/ (screen|screening) /.test(n) && !/ failure /.test(n))) return "name";
  for (const p of EVENT_NAME_PATTERNS) if (p.test(n)) return "name";
  return null;
}

/** form id -> basis, for every form the deterministic rule recognises. */
export function proposeAsNeededByRule(forms: AsNeededForm[]): Record<string, AsNeededBasis> {
  const out: Record<string, AsNeededBasis> = {};
  for (const f of forms ?? []) {
    if (typeof f?.id !== "string" || typeof f?.name !== "string") continue;
    const basis = asNeededBasisOf(f);
    if (basis) out[f.id] = basis;
  }
  return out;
}

const isSource = (v: unknown): v is AsNeededSource => v === "proposed" || v === "person";
const isBasis = (v: unknown): v is AsNeededBasis => v === "name" || v === "category" || v === "model";

/** The stored as-needed fields of a mapping, with anything malformed left out. */
export function asNeededOf(
  mapping: { asNeeded?: unknown; asNeededBasis?: unknown; asNeededCleared?: unknown } | null | undefined,
): AsNeededRecord {
  const asNeeded: Record<string, AsNeededSource> = {};
  if (mapping?.asNeeded && typeof mapping.asNeeded === "object" && !Array.isArray(mapping.asNeeded)) {
    for (const [id, v] of Object.entries(mapping.asNeeded as Record<string, unknown>)) if (isSource(v)) asNeeded[id] = v;
  }
  const asNeededBasis: Record<string, AsNeededBasis> = {};
  if (mapping?.asNeededBasis && typeof mapping.asNeededBasis === "object" && !Array.isArray(mapping.asNeededBasis)) {
    for (const [id, v] of Object.entries(mapping.asNeededBasis as Record<string, unknown>)) if (isBasis(v)) asNeededBasis[id] = v;
  }
  const asNeededCleared = (Array.isArray(mapping?.asNeededCleared) ? (mapping!.asNeededCleared as unknown[]) : [])
    .filter((x): x is string => typeof x === "string");
  return { asNeeded, asNeededBasis, asNeededCleared: [...new Set(asNeededCleared)].sort() };
}

/**
 * What to store as the as-needed fields when a person Saves.
 *
 * `previous` is the last SAVED mapping; `onScreen` the as-needed forms shown on
 * the grid being saved (form id -> where it came from, "proposed" until the
 * person touches it); `nextTicks` the grid's ticks (visit id -> form ids).
 *
 *   - A form still shown keeps its source and, if proposed, its basis.
 *   - A form that WAS as needed and no longer is, is remembered as cleared:
 *     whether the person switched it off or ticked a visit for it (ticking a
 *     visit clears as-needed), a later fill never proposes it again.
 *   - A form shown as needed leaves the cleared list.
 *   - A form shown as needed AND ticked on a visit is not as needed: the tick
 *     wins on Save (the page also clears it on screen).
 */
export function reconcileAsNeededOnSave(
  previous: { asNeeded?: unknown; asNeededBasis?: unknown; asNeededCleared?: unknown } | null | undefined,
  onScreen: Record<string, AsNeededSource>,
  nextTicks: Record<string, string[]>,
): AsNeededRecord {
  const prev = asNeededOf(previous);
  const ticked = new Set<string>();
  for (const ids of Object.values(nextTicks ?? {})) for (const id of ids) ticked.add(id);

  const asNeeded: Record<string, AsNeededSource> = {};
  const asNeededBasis: Record<string, AsNeededBasis> = {};
  for (const [id, source] of Object.entries(onScreen ?? {})) {
    if (!isSource(source) || ticked.has(id)) continue;
    asNeeded[id] = source;
    // The basis is about a PROPOSAL; it stays only while the form is still the platform's.
    if (source === "proposed" && prev.asNeeded[id] === "proposed" && prev.asNeededBasis[id]) {
      asNeededBasis[id] = prev.asNeededBasis[id];
    }
  }

  const cleared = new Set(prev.asNeededCleared);
  for (const id of Object.keys(prev.asNeeded)) if (!(id in asNeeded)) cleared.add(id);
  for (const id of Object.keys(asNeeded)) cleared.delete(id);
  return { asNeeded, asNeededBasis, asNeededCleared: [...cleared].sort() };
}

/** The as-needed form ids of a mapping, sorted. */
export function asNeededFormIds(mapping: { asNeeded?: unknown } | null | undefined): string[] {
  return Object.keys(asNeededOf(mapping).asNeeded).sort();
}

/** What readers that list forms by visit call the group. */
export const AS_NEEDED_LABEL = "As needed";

/**
 * The visits of a mapping with one more entry after them, "As needed", holding
 * the as-needed forms, for the readers that list forms BY VISIT (the annotated
 * CRF's bookmarks, the exports): such a reader shows the form under "As needed"
 * instead of silently dropping it. A form that is also on a real visit is not
 * repeated there. A study with no as-needed form gets its visits back unchanged.
 */
export function withAsNeededVisit<V extends { forms?: unknown }>(
  visits: V[],
  mapping: { asNeeded?: unknown } | null | undefined,
): Array<V | { id: string; name: string; forms: string[] }> {
  const onVisits = new Set<string>();
  for (const v of visits ?? []) for (const id of Array.isArray(v?.forms) ? (v.forms as unknown[]) : []) if (typeof id === "string") onVisits.add(id);
  const ids = asNeededFormIds(mapping).filter((id) => !onVisits.has(id));
  if (ids.length === 0) return [...(visits ?? [])];
  return [...(visits ?? []), { id: "as-needed", name: AS_NEEDED_LABEL, forms: ids }];
}
